/**
 * StudyDetail Page
 * Tabbed detail view for a single study
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Users, Link as LinkIcon, UserPlus, FileText } from 'lucide-react';
import api from '../../services/api';
import { useStudyOrganizer } from '../contexts';
import { useProtocolDetail } from '../hooks';
import {
  StudyHeader,
  TeamTab,
  BookmarksTab,
  ExternalContactsTab,
  DocumentsTab,
} from '../components';

type TabType = 'team' | 'bookmarks' | 'contacts' | 'documents';

export function StudyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isStarred, toggleStar } = useStudyOrganizer();
  const [activeTab, setActiveTab] = useState<TabType>('team');
  const [documentCount, setDocumentCount] = useState(0);

  const fetchDocumentCount = useCallback(async () => {
    if (!id) return;
    try {
      // Registry (eReg) docs + legacy Intelligence uploads not yet adopted —
      // the same rule DocumentsTab renders (plan §5.3).
      const [registryRes, legacyRes] = await Promise.all([
        api.get(`/api/registry/studies/${id}/documents`).catch(() => ({ data: { documents: [] } })),
        api.get(`/api/intelligence/protocols/${id}/documents`).catch(() => ({ data: [] })),
      ]);
      const legacy = (legacyRes.data || []).filter((d: any) => !d.registry_document_id);
      const registryDocs = (registryRes.data?.documents || []).filter((d: any) => d.status !== 'archived');
      setDocumentCount(registryDocs.length + legacy.length);
    } catch {
      // silently fail — count stays 0
    }
  }, [id]);

  useEffect(() => {
    fetchDocumentCount();
  }, [fetchDocumentCount]);

  const {
    protocol,
    teamMembers,
    bookmarks,
    contacts,
    siteUsers,
    loading,
    teamLoading,
    bookmarksLoading,
    contactsLoading,
    error,
    updateProtocol,
    addTeamMember,
    updateTeamMember,
    removeTeamMember,
    addBookmark,
    updateBookmark,
    removeBookmark,
    reorderBookmarks,
    assignments,
    assignBookmarkUser,
    unassignBookmarkUser,
    addContact,
    updateContact,
    removeContact,
  } = useProtocolDetail({ protocolId: id || '' });

  if (!id) {
    return <Navigate to="/siteorganizer" replace />;
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  if (error || !protocol) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Study not found'}</p>
          <a
            href="/siteorganizer"
            className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
          >
            Back to Studies
          </a>
        </div>
      </div>
    );
  }

  const tabs: { key: TabType; label: string; count: number; icon: React.ReactNode }[] = [
    { key: 'team', label: 'Study Team', count: teamMembers.length, icon: <Users size={16} /> },
    { key: 'bookmarks', label: 'Bookmarks', count: bookmarks.length, icon: <LinkIcon size={16} /> },
    { key: 'contacts', label: 'External Contacts', count: contacts.length, icon: <UserPlus size={16} /> },
    { key: 'documents', label: 'Documents', count: documentCount, icon: <FileText size={16} /> },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Study Header */}
      <StudyHeader
        protocol={protocol}
        isStarred={isStarred(protocol.id)}
        onToggleStar={() => toggleStar(protocol.id)}
        onUpdateStatus={async (status) => {
          await updateProtocol({ enrollment_status: status });
        }}
        onArchive={async () => {
          const newStatus = protocol.status === 'inactive' ? 'active' : 'inactive';
          await updateProtocol({ status: newStatus });
          if (newStatus === 'inactive') {
            navigate('/siteorganizer');
          }
        }}
      />

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 sm:gap-6 overflow-x-auto pb-px">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex min-h-[44px] flex-shrink-0 items-center gap-1 sm:gap-1.5 whitespace-nowrap border-b-2 px-2.5 text-sm font-medium transition-colors duration-150 sm:min-h-0 sm:py-2 ${
                  activeTab === tab.key
                    ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <span className="hidden sm:inline">{tab.icon}</span>
                {tab.label}
                <span className={`ml-1 rounded-full px-1.5 py-0.5 text-xs ${
                  activeTab === tab.key
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'team' && (
          <TeamTab
            teamMembers={teamMembers}
            siteUsers={siteUsers}
            loading={teamLoading}
            onAddMember={addTeamMember}
            onUpdateMember={updateTeamMember}
            onRemoveMember={removeTeamMember}
          />
        )}

        {activeTab === 'bookmarks' && (
          <BookmarksTab
            bookmarks={bookmarks}
            loading={bookmarksLoading}
            onAddBookmark={addBookmark}
            onUpdateBookmark={updateBookmark}
            onRemoveBookmark={removeBookmark}
            onReorderBookmarks={reorderBookmarks}
            assignments={assignments}
            siteUsers={siteUsers}
            onAssignUser={assignBookmarkUser}
            onUnassignUser={unassignBookmarkUser}
          />
        )}

        {activeTab === 'contacts' && (
          <ExternalContactsTab
            contacts={contacts}
            loading={contactsLoading}
            onAddContact={addContact}
            onUpdateContact={updateContact}
            onRemoveContact={removeContact}
          />
        )}

        {activeTab === 'documents' && (
          <DocumentsTab protocolId={id} onDocumentCountChange={setDocumentCount} />
        )}
        </div>
      </div>
    </div>
  );
}

export default StudyDetail;
